/**
 * 월드 지형 렌더 — 완만한 지형 메시 + 실시간 길(캔버스 텍스처) + 나무/마왕성/깃발.
 * 프로토타입 셰이더 이식. 길 상하반전 버그(§8-②)는 flipY=false로 픽스.
 */
import * as THREE from 'three';
import { WORLD } from '@villager/shared';
import { colX, frontZ, heightAt, rowZ, TERR_D, TERR_W, type World } from '@villager/sim-core';

export interface Terrain {
  worldGroup: THREE.Group;
  terrainMesh: THREE.Mesh;
  paintPath(x: number, z: number, r: number, a: number): void;
  rebuildFlags(frontRow: number): void;
}

const { GW, GD, TILE } = WORLD;

export function createTerrain(w: World): Terrain {
  const worldGroup = new THREE.Group();

  // ── 길 페인팅 캔버스 ──
  const PC = 1024;
  const pcv = document.createElement('canvas');
  pcv.width = PC;
  pcv.height = PC;
  const pctx = pcv.getContext('2d')!;
  pctx.fillStyle = '#000';
  pctx.fillRect(0, 0, PC, PC);
  const pathTex = new THREE.CanvasTexture(pcv);
  pathTex.flipY = false; // 캔버스 좌표 ↔ 셰이더 UV 일치 (상하반전 버그 픽스)

  function paintPath(x: number, z: number, r: number, a: number): void {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return;
    const u = ((x + TERR_W / 2) / TERR_W) * PC;
    const v = ((z + TERR_D / 2) / TERR_D) * PC;
    const g = pctx.createRadialGradient(u, v, 0, u, v, r);
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    pctx.fillStyle = g;
    pctx.beginPath();
    pctx.arc(u, v, r, 0, 7);
    pctx.fill();
    pathTex.needsUpdate = true;
  }

  // ── 지형 메시 ──
  const tg = new THREE.PlaneGeometry(TERR_W, TERR_D, 120, 168);
  tg.rotateX(-Math.PI / 2);
  {
    const pos = tg.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) pos.setY(i, heightAt(pos.getX(i), pos.getZ(i)));
    tg.computeVertexNormals();
  }
  const terrMat = new THREE.ShaderMaterial({
    uniforms: {
      pathTex: { value: pathTex },
      lightDir: { value: new THREE.Vector3(0.5, 0.9, 0.35).normalize() },
      uFront: { value: frontZ(w.frontRow) },
      uTile: { value: TILE },
      uHalf: { value: new THREE.Vector2(TERR_W / 2, TERR_D / 2) },
    },
    vertexShader: `varying vec3 vW; varying vec3 vN;
      void main(){ vW=(modelMatrix*vec4(position,1.)).xyz; vN=normalize(normalMatrix*normal);
        gl_Position=projectionMatrix*viewMatrix*vec4(vW,1.); }`,
    fragmentShader: `
      uniform sampler2D pathTex; uniform vec3 lightDir; uniform float uFront; uniform float uTile; uniform vec2 uHalf;
      varying vec3 vW; varying vec3 vN;
      float h21(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
      void main(){
        float t=clamp((vW.z-uFront)/40.,-1.,1.);
        vec3 grass=vec3(.42,.53,.30), dry=vec3(.55,.52,.34), ash=vec3(.30,.24,.33);
        vec3 base = t>0. ? mix(dry,grass,clamp(t,0.,1.)) : mix(dry,ash,clamp(-t,0.,1.));
        base*=0.93+0.14*h21(floor(vW.xz*1.7));
        vec2 g=abs(fract((vW.xz+uHalf)/uTile)-0.5);
        float edge=smoothstep(0.47,0.5,max(g.x,g.y));
        if(vW.z>uFront) base=mix(base,base*0.78,edge*0.9);
        vec2 uv=(vW.xz+uHalf)/(uHalf*2.0);
        float p=texture2D(pathTex,uv).r;
        base=mix(base,vec3(.58,.45,.30),smoothstep(.08,.55,p));
        float li=clamp(dot(normalize(vN),lightDir),0.,1.);
        gl_FragColor=vec4(base*(0.55+0.6*li),1.);
      }`,
  });
  const terrainMesh = new THREE.Mesh(tg, terrMat);
  worldGroup.add(terrainMesh);

  // ── 나무 (숲 타일 기반, 전선 이북은 죽은 나무) ──
  {
    const foliG = new THREE.ConeGeometry(1.5, 3.4, 6);
    const trunkG = new THREE.CylinderGeometry(0.26, 0.34, 1.1, 5);
    const deadG = new THREE.ConeGeometry(0.65, 4, 5);
    const foliM = new THREE.MeshLambertMaterial({ color: 0x3d5a30 });
    const trunkM = new THREE.MeshLambertMaterial({ color: 0x5a4028 });
    const deadM = new THREE.MeshLambertMaterial({ color: 0x3a2c3e });
    const spots: [number, number][] = [];
    const deadSpots: [number, number][] = [];
    for (let c = 0; c < GW; c++) {
      for (let r = 0; r < GD; r++) {
        if (!w.grid[c]![r]!.forest) continue;
        const n = 3 + Math.floor(Math.random() * 4);
        for (let i = 0; i < n; i++) {
          const x = colX(c) + (Math.random() - 0.5) * TILE * 0.8;
          const z = rowZ(r) + (Math.random() - 0.5) * TILE * 0.8;
          if (r >= w.frontRow) spots.push([x, z]);
          else deadSpots.push([x, z]);
        }
      }
    }
    const mk = (
      geo: THREE.BufferGeometry,
      mat: THREE.Material,
      arr: [number, number][],
      yOff: number,
      sMin: number,
      sMax: number,
    ): void => {
      if (!arr.length) return;
      const im = new THREE.InstancedMesh(geo, mat, arr.length);
      const M = new THREE.Matrix4();
      const Q = new THREE.Quaternion();
      const S = new THREE.Vector3();
      const P = new THREE.Vector3();
      arr.forEach((p, i) => {
        const s = sMin + Math.random() * (sMax - sMin);
        P.set(p[0], heightAt(p[0], p[1]) + yOff * s, p[1]);
        Q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.random() * 6.28);
        S.set(s, s, s);
        M.compose(P, Q, S);
        im.setMatrixAt(i, M);
      });
      worldGroup.add(im);
    };
    mk(foliG, foliM, spots, 2.4, 0.7, 1.4);
    mk(trunkG, trunkM, spots, 0.55, 0.7, 1.4);
    mk(deadG, deadM, deadSpots, 2, 0.6, 1.2);
  }

  // ── 마왕성 ──
  {
    const m = new THREE.MeshLambertMaterial({ color: 0x241a2c });
    const g = new THREE.Group();
    (
      [
        [0, 0, 14, 24, 9],
        [-8, 0, 5, 15, 5],
        [8, 0, 5, 15, 5],
      ] as const
    ).forEach((s) => {
      const box = new THREE.Mesh(new THREE.BoxGeometry(s[2], s[3], s[4]), m);
      box.position.set(s[0], s[3] / 2, s[1]);
      g.add(box);
    });
    const sp = new THREE.Mesh(new THREE.ConeGeometry(2.6, 10, 4), m);
    sp.position.y = 27;
    g.add(sp);
    g.position.set(0, heightAt(0, rowZ(0)), rowZ(0));
    worldGroup.add(g);
  }

  // ── 전선 깃발 (재구성 가능) ──
  const flagGroup = new THREE.Group();
  worldGroup.add(flagGroup);
  function rebuildFlags(frontRow: number): void {
    flagGroup.clear();
    const z = frontZ(frontRow);
    for (let x = -TERR_W / 2 + 8; x <= TERR_W / 2 - 8; x += 20) {
      const g = new THREE.Group();
      const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.12, 0.12, 5, 4),
        new THREE.MeshLambertMaterial({ color: 0x4a3a2a }),
      );
      pole.position.y = 2.5;
      g.add(pole);
      const f = new THREE.Mesh(
        new THREE.PlaneGeometry(2.2, 1.2),
        new THREE.MeshBasicMaterial({ color: 0xc04a2e, side: THREE.DoubleSide }),
      );
      f.position.set(1.1, 4.2, 0);
      g.add(f);
      g.position.set(x + Math.random() * 5, heightAt(x, z), z + Math.random() * 3 - 1.5);
      flagGroup.add(g);
    }
    terrMat.uniforms.uFront!.value = z;
  }
  rebuildFlags(w.frontRow);

  return { worldGroup, terrainMesh, paintPath, rebuildFlags };
}
